/*
 * CameraTransition contains reusable helpers for animated deck.gl camera moves.
 *
 * It wraps deck.gl transition props so viewer code can request a smooth camera
 * move without duplicating interpolator setup or transition cleanup.
 */
import {FlyToInterpolator} from "@deck.gl/core";

const DEFAULT_CAMERA_TRANSITION_DURATION = 325;
const DEFAULT_FLY_TO_OPTIONS = {
    speed: 1.6,
    curve: 1.414,
    maxDuration: 1200,
};

/**
 * Adds deck.gl fly-to transition props to a target view state.
 *
 * @param {object} viewState Target deck.gl view state.
 * @param {object} [options] Transition options.
 * @param {number|string} [options.duration=325] Transition duration in milliseconds, or "auto".
 * @param {object} [options.flyToOptions] Options passed to FlyToInterpolator.
 * @param {Function} [options.onEnd] Callback called by deck.gl when the transition ends.
 * @returns {object} View state with transition props.
 */
export function withCameraTransition(viewState, options = {}) {
    return {
        ...stripCameraTransition(viewState),
        transitionDuration: options.duration ?? DEFAULT_CAMERA_TRANSITION_DURATION,
        transitionInterpolator: new FlyToInterpolator({
            ...DEFAULT_FLY_TO_OPTIONS,
            ...(options.flyToOptions || {}),
        }),
        ...(options.onEnd ? {onTransitionEnd: options.onEnd} : {}),
    };
}

/**
 * Removes deck.gl transition props from a view state before storing it.
 *
 * @param {object|null|undefined} viewState Source deck.gl view state.
 * @returns {object|null} View state without transition callbacks and interpolator objects.
 */
export function stripCameraTransition(viewState) {
    if (!viewState) {
        return null;
    }

    const {
        transitionDuration,
        transitionInterpolator,
        transitionEasing,
        transitionInterruption,
        onTransitionStart,
        onTransitionEnd,
        onTransitionInterrupt,
        ...rest
    } = viewState;

    return {...rest};
}

/**
 * Returns the default duration used by camera transition helpers.
 *
 * @returns {number} Transition duration in milliseconds.
 */
export function defaultCameraTransitionDuration() {
    return DEFAULT_CAMERA_TRANSITION_DURATION;
}
